import { useForm } from 'react-hook-form'
import { Form, useTransition } from '@remix-run/react'
import type { getJob } from '~/models/job.server'
import type { Prisma } from '@prisma/client'
import type { HTMLInputTypeAttribute } from 'react'
import { UploadInput } from './UploadInput'
import classnames from 'classnames'

import type { ErrorsList } from '~/types'

type SelectOption = {
  value: string
  label: string
}

type FormField = {
  question_text: string
  question_order: string
  question_format: string
  question_status: string
  question_answers: string
  questionnaire_id: string
  question_mandatory: string
  questionnaire_code: string
  question_correct_answer: string
}

type InputType = {
  type: HTMLInputTypeAttribute
  attrs: {
    [key: string]: string | number | boolean | SelectOption[]
  }
}

type RenderField = {
  name: string
  label: string
  type: InputType
  isCustom?: boolean
  isRequired?: boolean
}

function getInputType(field: FormField): InputType {
  const { question_format } = field

  if (field.question_format === 'Text Field') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
      },
    }
  }

  if (field.question_format === 'Date') {
    return {
      type: 'input',
      attrs: {
        type: 'text',
        hint: 'YYYY-MM-DD',
      },
    }
  }

  if (field.question_format === 'Drop-down Menu') {
    return {
      type: 'select',
      attrs: {
        options: field.question_answers
          .split('\r\n')
          .map((option) => ({ value: option, label: option })),
      },
    }
  }

  if (question_format === 'Text Box (Multi-line)') {
    return {
      type: 'textarea',
      attrs: {
        rows: 5,
      },
    }
  }

  if (question_format === 'Number') {
    return {
      type: 'input',
      attrs: {
        type: 'number',
      },
    }
  }

  if (question_format === 'Checkbox') {
    return {
      type: 'checkboxes',
      attrs: {
        options: field.question_answers
          .split('\r\n')
          .map((option) => ({ value: option, label: option })),
      },
    }
  }

  if (question_format === 'File Upload') {
    return {
      type: 'input',
      attrs: {
        type: 'file',
      },
    }
  }

  return {
    type: 'input',
    attrs: {
      type: 'text',
    },
  }
}

export const ApplicationFormField = ({
  field,
  errors,
}: {
  field: RenderField
  errors?: string[]
}) => {
  const fieldName = field.isCustom ? `answers.${field.name}` : field.name

  if (
    field.type.attrs.type === 'file' &&
    field.label.toLowerCase() !== 'photo'
  ) {
    return <UploadInput field={field} name={fieldName} errors={errors} />
  }

  if (field.type.type === 'textarea') {
    return (
      <div className="mt-8">
        <label
          htmlFor="about"
          className="block text-sm font-medium text-gray-700"
        >
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <textarea
            name={fieldName}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
            placeholder=""
            defaultValue={''}
          />
        </div>
        {/* <p className="mt-2 text-sm text-gray-500">Max 1000 characters</p> */}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors.join(', ')}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'none') {
    return null
  }

  if (field.type.type === 'boolean') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {errors.join(', ')}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (
    field.type.type === 'checkboxes' &&
    Array.isArray(field.type.attrs.options)
  ) {
    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        {field.type.attrs.options.map((option: SelectOption, i) => (
          <div key={option.value} className="relative mt-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                id={`${fieldName}-${option.value}`}
                name={`${fieldName}.${i}`}
                value={option.value}
                type={field.type.attrs.singleAnswer ? 'radio' : 'checkbox'}
                className={classnames(
                  'h-4 w-4 border-gray-300 text-accent-color focus:ring-accent-color',
                  field.type.attrs.singleAnswer ? 'rounded-full' : 'rounded'
                )}
              />
            </div>
            <div className="ml-3 text-sm">
              <label
                htmlFor={`${fieldName}-${option.value}`}
                className="font-normal text-gray-700"
              >
                {option.label}
              </label>
            </div>
          </div>
        ))}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors.join(', ')}
          </div>
        )}
      </div>
    )
  }

  if (field.type.type === 'select') {
    const options = field.type.attrs.options as SelectOption[]

    return (
      <div className="mt-8">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.isRequired && <span className="text-rose-500"> *</span>}
        </label>
        <div className="mt-1">
          <select
            name={fieldName}
            className="mt-1 block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-accent-color focus:outline-none focus:ring-accent-color sm:text-sm"
          >
            <option value="">- select -</option>
            {options.map((option: any) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors && (
            <div className="pt-1 text-red-700" id="password-error">
              {errors.join(', ')}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="mt-8">
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.isRequired && <span className="text-rose-500"> *</span>}
      </label>
      <div className="mt-1">
        <input
          {...field.type.attrs}
          name={fieldName}
          id={field.name}
          autoComplete="off"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-400 sm:text-sm"
        />
        {field.type.attrs.hint && (
          <p className="mt-2 text-sm text-gray-500">
            {field.type.attrs.hint as string}
          </p>
        )}
        {errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {errors.join(', ')}
          </div>
        )}
      </div>
    </div>
  )
}

export const ApplicationForm = ({
  job,
  errors,
}: {
  job: Awaited<ReturnType<typeof getJob>>
  errors: ErrorsList
}) => {
  const transition = useTransition()
  const fields: RenderField[] = []

  const data = (job?.applicationForm?.data || []) as FormField[]

  // const formFields = data.form_fields as FormField[]

  fields.push({
    name: 'first_name',
    label: 'First name',
    type: { type: 'input', attrs: { type: 'text' } },
    isRequired: true,
  })

  fields.push({
    name: 'last_name',
    label: 'Last name',
    type: { type: 'input', attrs: { type: 'text' } },
    isRequired: true,
  })

  fields.push({
    name: 'email',
    label: 'Email address',
    type: { type: 'input', attrs: { type: 'email' } },
    isRequired: true,
  })

  fields.push({
    name: 'linkedin',
    label: 'LinkedIn profile URL',
    type: { type: 'input', attrs: { type: 'text' } },
    isRequired: true,
  })

  // fields.push({
  //   name: "phone",
  //   label: "Phone number",
  //   type: { type: "input", attrs: { type: "text" } },
  // })

  fields.push({
    name: 'base64-resume',
    label: 'Resume',
    type: { type: 'input', attrs: { type: 'file' } },
    isRequired: true,
  })

  for (let field of data) {
    fields.push({
      name: `answers.${field.question_order}`,
      label: field.question_text,
      type: getInputType(field),
      isRequired: field.question_mandatory.toLowerCase() === 'yes',
    })
  }

  return (
    <div>
      <Form method="post" encType="multipart/form-data">
        {fields.map((field, i) => (
          <ApplicationFormField
            key={i}
            field={field}
            errors={errors ? errors[field.name] : undefined}
          />
        ))}
        <div className="pt-12">
          <div className="flex justify-center">
            <button
              type="submit"
              className="inline-flex justify-center rounded-md border border-transparent bg-accent-color py-3 px-8 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {transition.state === 'submitting'
                ? 'Submitting...'
                : 'Submit application'}
            </button>
          </div>
        </div>
      </Form>
    </div>
  )
}
